/**
 * Pure helpers for the encounter Sign & Lock surface (PROD-1282).
 *
 * Faithful to Flutter `wrap_up_billing_screen` + `patient_encounter_bloc`:
 *   - nurse sign  → a sign record, no lock  ("nurse-signed", Flutter isSignedByNurse)
 *   - doctor sign → sign + lock             ("locked",       Flutter isSignAndLocked)
 * The doctor sign is gated on a diagnosis being present (bloc signNote: "Please add
 * at-least one diagnosis before signing encounter").
 */
import type { SignLockModel } from '@/types/sign-lock-api';
import { parseNoZIsoDatetime } from '@/shared/lib/date-format';

export type SignLockState = 'draft' | 'nurse-signed' | 'locked';

/** Derive the encounter's sign state from its sign-lock records. */
export function deriveSignLockState(records: SignLockModel[] | null | undefined): SignLockState {
  const list = records ?? [];
  if (list.some((r) => r.lock === true)) return 'locked';
  if (list.some((r) => r.sign === true)) return 'nurse-signed';
  return 'draft';
}

/**
 * Is the note locked by a PROVIDER sign — the fact QuestionBank's server-side lock keys on (PROD-2547)?
 *
 * Deliberately NOT `deriveSignLockState(...) === 'locked'`: the backend never persists `lock` on a
 * sign record (CreateSignLockAsync ignores it), so a doctor's sign reads as 'nurse-signed' there.
 * The server's rule is "a sign record whose IsSignedByNurse is false", so this mirrors that rule.
 * A nurse-only sign does not lock the note: the doctor still documents after it with ordinary saves.
 */
export function isProviderSigned(records: SignLockModel[] | null | undefined): boolean {
  // Keys on `isSignedByNurse` ONLY, never on `lock`: it stays correct if PROD-2579 starts persisting
  // `lock`. Revisit if the GET ever stops returning `isSignedByNurse` (absent is read as a provider
  // sign, matching the server's default), and keep in step with Scrips.Patient GetEncounterSignState.
  return (records ?? []).some((r) => r.isSignedByNurse !== true);
}

/**
 * NOTE WORDING CUTOFF (PROD-2786). A note PROVIDER-signed before this instant was signed under the
 * old note wording ("Fever: No", "HbA1c: 7.2") and keeps rendering in it; a note signed at or after
 * it was reviewed in the new wording ("Denies Fever", "HbA1c: 7.2 %") and keeps that. The note is
 * re-derived from the answers on every view, so this is what stops a signed note changing on view
 * (contract G1 / G17) without flipping a note signed after the change.
 *
 * REMOVED WHEN PROD-2810 (freeze the note text at sign) LANDS: a signed note then renders from its
 * frozen text, and neither this constant nor `signedUnderLegacyNoteWording` has a job.
 */
export const NOTE_WORDING_CUTOFF_UTC = '2026-10-01T06:30:00Z';

/**
 * Must this note render with the pre-PROD-2786 formatter? True only for a PROVIDER-signed note
 * (the `isProviderSigned` rule) whose sign time is before `cutoffUtc`.
 *
 * - The sign time is each provider sign record's `createdDate` (`SignLockModel.createdDate`). The
 *   backend stores UTC and the GET converts it (`ConvertTimeFromUtc`) into the zone named by the
 *   request's `userTimeZone` header -- which `api-client.ts` sets to THIS browser's own IANA zone --
 *   and sends it without a `Z`. So reading it as browser-local (`parseNoZIsoDatetime`, the same
 *   reader the "Signed <time>" attestation uses) recovers the exact instant.
 * - Legacy unless EVERY provider sign has a readable time at or after the cutoff. The earliest
 *   provider sign is when the note was first attested (the backend's `SignedAtUtc` is that same
 *   minimum), so a later amendment never moves a note to the new wording.
 * - A missing or unreadable sign time on a provider sign means LEGACY: it protects an old note.
 * - Unsigned, or nurse-only signed: false (the new wording).
 */
export function signedUnderLegacyNoteWording(
  records: SignLockModel[] | null | undefined,
  cutoffUtc: string = NOTE_WORDING_CUTOFF_UTC,
): boolean {
  const cutoff = Date.parse(cutoffUtc);
  return (records ?? [])
    .filter((r) => r.isSignedByNurse !== true)
    .some((r) => {
      const signedAt = parseNoZIsoDatetime(r.createdDate?.trim());
      return signedAt === null || signedAt.getTime() < cutoff;
    });
}

/**
 * When the encounter was signed — the `createdDate` of the record that PRODUCED the current state,
 * so the timestamp always agrees with the label `deriveSignLockState` returns above.
 *
 * Feeds the note rail's "Signed <timestamp>" attestation. Returns null for a draft encounter, and
 * ALSO for a signed one whose record carries no `createdDate` (the field is nullable on the wire) —
 * the caller must render no attestation rather than invent a date. Never falls back to "now": the
 * signing may have happened in an earlier session, and a fabricated time on a clinical attestation
 * is a records-integrity problem, not a cosmetic one.
 */
export function signLockSignedAt(records: SignLockModel[] | null | undefined): string | null {
  const list = records ?? [];
  // Same precedence as deriveSignLockState: a lock record wins over a bare sign record.
  const record = list.find((r) => r.lock === true) ?? list.find((r) => r.sign === true);
  return record?.createdDate?.trim() || null;
}

/** Banner label + DS semantic for a sign state (rendered via EncounterStatusTag). */
export function signLockBanner(state: SignLockState): { label: string; semantic: 'success' | 'warning' | 'info' } {
  switch (state) {
    case 'locked':
      return { label: 'Signed & Locked', semantic: 'success' };
    case 'nurse-signed':
      return { label: 'Nurse-signed', semantic: 'warning' };
    default:
      return { label: 'Draft', semantic: 'info' };
  }
}

export interface SignLockRequestInput {
  encounterId: string;
  patientId: string;
  performerId: string;
  /**
   * May this signer FINALISE the encounter (sign + lock), or only sign?
   *
   * A POSITIVE capability, supplied by `canLockEncounter` (src/auth/use-role.ts) — never
   * `!isNurse`. Locking is irreversible, so it must be granted by recognising the signer as
   * someone allowed to finalise, not by failing to recognise them as a nurse: under the old
   * `!isNurse` shape a degraded profile (`role: undefined`), an allied-health persona
   * (`role '2'` on a non-nurse tenant role) and any future staff persona all LOCKED. Doctors
   * and nurses — the whole population today — are unaffected by the change.
   */
  canLock: boolean;
  /**
   * The encounter's A&P document id (Flutter `apData.identifier`, patient_encounter_bloc.dart).
   * Patient stores it on the sign-lock row as PatientAssessmentPlanId, and NABIDH/Malaffi build
   * DG1, PR1, ORM, VXU and the MDM note PDF only when every recent sign row carries it.
   * Null when the encounter has no A&P yet — the same as Flutter.
   */
  assessmentPlanId: string | null;
  note?: string;
}

/** Build the CreateSignLock body. Always signs; locks only when the signer may finalise. */
export function toSignLockRequest(input: SignLockRequestInput): SignLockModel {
  return {
    subject: input.patientId,
    encounter: input.encounterId,
    performer: input.performerId,
    sign: true,
    lock: input.canLock,
    assessmentPlanId: input.assessmentPlanId,
    note: input.note ?? null,
    isVaccinationSign: false,
  };
}
